/**
 * useHubUpload
 *
 * Reusable hook for uploading files through the Document Hub pipeline:
 *   hash → createBatch → blob upload → completeBatch →
 *   waitForHubReady (scan/promote) → waitForDocumentIntelligence → onFileReady
 *
 * Call ``uploadFiles([file])`` for a single file or ``uploadFiles(files)``
 * for a batch — both share the same code path (one batch per call).
 *
 * Key options:
 * - ``skipDI`` — return ``true`` to skip waiting for Document Intelligence
 *   (defaults to skipping for image content types).
 * - ``onFileReady`` — called per-file once promoted + DI complete; use for
 *   chat registration, writing-upload registration, etc.
 *
 * @example
 * ```tsx
 * const hub = useHubUpload({
 *   documentType: 'chat_upload',
 *   skipDI: (_f, ct) => IMAGE_MIME_TYPES.has(ct),
 *   onFileReady: async (documentId, file, key) => {
 *     const reg = await registerHubDocumentForChat(documentId);
 *     setRegistration(key, reg);
 *   },
 * });
 *
 * hub.uploadFiles([file]);            // single
 * hub.uploadFiles([f1, f2, f3]);      // batch
 * ```
 */

import { useCallback, useRef, useState } from 'react';
import { uploadBatchApi, waitForHubReady, waitForDocumentIntelligence, HUB_FAILED_STATUSES } from 'src/lib/api/files';
import {
  ACCEPTED_MIME_SET,
  computeSha256,
  FILE_IN_USE_MESSAGE,
  IMAGE_MIME_TYPES,
  inferContentType,
  formatUploadErrorDetail,
  parseBatchValidationFileName,
  snapshotLocalFile,
  validateFile,
  validationErrorMessage
} from './utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const UPLOAD_CONCURRENCY = 5;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UploadTargetContext {
  groupId?: string;
  pamQuestionId?: string;
  pamSiteId?: string;
  /**
   * When true, uploaded files are added to the organisation library so they
   * become reusable organisational documents (visible to the whole tenant).
   * Available to any non-guest member.
   */
  addToOrganisation?: boolean;
}

export type HubUploadStage = 'uploading' | 'scanning' | 'processing' | 'ready' | 'error';

export interface HubFileState {
  file: File;
  stage: HubUploadStage;
  /** 0–100 while stage === 'uploading'; fixed at 100 afterwards. */
  uploadProgress: number;
  /** Populated once the file reaches 'ready'. */
  documentId?: string;
  contentType: string;
  isImage: boolean;
  /** Blob object URL for image previews; revoked on remove/clear. */
  previewUrl?: string;
  error?: string;
}

export interface UseHubUploadOptions {
  /**
   * Document-type tag passed to the hub batch creation endpoint
   * (e.g. ``'chat_upload'``, ``'writing_upload'``, ``'document_hub'``).
   */
  documentType?: string;

  /**
   * Called once the file is promoted and DI has completed (or been skipped).
   * Awaited before the file stage changes to ``'ready'``.
   * Throwing marks the file as ``'error'``.
   */
  onFileReady?: (documentId: string, file: File, key: string) => Promise<void>;

  /**
   * Return ``true`` to skip waiting for Document Intelligence for a given
   * file. Defaults to skipping for image content types.
   */
  skipDI?: (file: File, contentType: string) => boolean;

  /**
   * Called once blobs are uploaded and the batch has been submitted to the
   * backend for scan/promote (after ``completeBatchUploads``). Useful for
   * persisting the batch ID so progress can be resumed after a page refresh.
   */
  onBatchReady?: (batchId: string, files: { key: string; fileId: string; name: string; size: number }[]) => void;
}

export interface ClearUploadsOptions {
  /**
   * When ``true``, keep files whose upload is still in-flight and only remove
   * completed/errored ones. Useful when submitting a chat message while some
   * files are still processing.
   */
  keepInflight?: boolean;
  /**
   * Whether to revoke blob object URLs for image previews.
   * Defaults to ``true``.
   */
  revokePreviewUrls?: boolean;
}

export interface UseHubUploadReturn {
  /** Live per-file state keyed by the opaque key returned by ``uploadFiles``. */
  uploads: Map<string, HubFileState>;
  /**
   * Upload one or more files in a single batch with bounded concurrency.
   * Returns the opaque keys (one per file, same order) used to track state
   * in ``uploads``.
   */
  uploadFiles: (
    files: File[],
    options?: UploadTargetContext & { concurrency?: number; documentType?: string }
  ) => string[];
  /** Cancel and remove a file (revokes any preview URL). */
  removeFile: (key: string) => void;
  /** Abort an in-flight upload without removing it from state. */
  cancelFile: (key: string) => void;
  /** Abort all in-flight uploads and clear all state. */
  clearUploads: (options?: ClearUploadsOptions) => void;
  /** Resolve once all current in-flight uploads finish (success or error). */
  waitForAll: () => Promise<void>;
  /** Read the current uploads map from a ref — safe to call inside async code. */
  getUploads: () => Map<string, HubFileState>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useHubUpload(options: UseHubUploadOptions = {}): UseHubUploadReturn {
  const { documentType = 'upload', onFileReady, skipDI, onBatchReady } = options;

  const onBatchReadyRef = useRef(onBatchReady);
  onBatchReadyRef.current = onBatchReady;

  const [uploads, _setUploads] = useState<Map<string, HubFileState>>(new Map());
  const uploadsRef = useRef<Map<string, HubFileState>>(uploads);

  const setUploads: typeof _setUploads = useCallback((action) => {
    _setUploads((prev) => {
      const next = typeof action === 'function' ? action(prev) : action;
      uploadsRef.current = next;
      return next;
    });
  }, []);

  const abortRef = useRef<Map<string, AbortController>>(new Map());
  const fileIdsRef = useRef<Map<string, string>>(new Map());
  const inflightRef = useRef<Map<string, Promise<void>>>(new Map());

  const patchFile = useCallback(
    (key: string, patch: Partial<HubFileState>) => {
      setUploads((prev) => {
        const existing = prev.get(key);
        if (!existing) return prev;
        const next = new Map(prev);
        next.set(key, { ...existing, ...patch });
        return next;
      });
    },
    [setUploads]
  );

  // -------------------------------------------------------------------------
  // uploadFiles — one or more files, one shared batch, bounded concurrency
  // -------------------------------------------------------------------------

  const uploadFiles = useCallback(
    (files: File[], opts?: UploadTargetContext & { concurrency?: number; documentType?: string }): string[] => {
      if (files.length === 0) return [];
      const concurrency = opts?.concurrency ?? UPLOAD_CONCURRENCY;
      const { groupId, pamQuestionId, pamSiteId, addToOrganisation } = opts ?? {};
      const callDocumentType = opts?.documentType ?? documentType;

      // Build per-file metadata
      const entries = files.map((file) => {
        const contentType = inferContentType(file);
        const isImage = IMAGE_MIME_TYPES.has(file.type) || IMAGE_MIME_TYPES.has(contentType);
        const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
        const key = `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return { key, file, contentType, isImage, previewUrl };
      });

      // Add all files to state immediately so the UI reflects them
      setUploads((prev) => {
        const next = new Map(prev);
        for (const { key, file, contentType, isImage, previewUrl } of entries) {
          next.set(key, { file, stage: 'uploading', uploadProgress: 0, contentType, isImage, previewUrl });
        }
        return next;
      });

      // One abort controller per file, so stopping one does not stop the others.
      const controllers = new Map<string, AbortController>();
      for (const { key } of entries) {
        const fileController = new AbortController();
        controllers.set(key, fileController);
        abortRef.current.set(key, fileController);
      }

      const promise = (async () => {
        try {
          // 1. Snapshot + hash (parallel) and validate per file.
          // Snapshotting copies bytes into memory so Word/Excel locks do not
          // break the later Azure PUT (which otherwise streams from disk).
          const hashed = await Promise.all(
            entries.map(async (e) => {
              try {
                const file = await snapshotLocalFile(e.file);
                return { ...e, file, sha256: await computeSha256(file) };
              } catch (err: unknown) {
                const message = err instanceof Error ? err.message : FILE_IN_USE_MESSAGE;
                patchFile(e.key, { stage: 'error', error: message });
                return null;
              }
            })
          );
          const withHashes = hashed.filter((entry): entry is NonNullable<typeof entry> => entry != null);

          const seenSha256 = new Set<string>();
          const uploadable: typeof withHashes = [];
          for (const entry of withHashes) {
            const validationError = validateFile(entry.file, seenSha256);
            if (validationError) {
              patchFile(entry.key, {
                stage: 'error',
                error: validationErrorMessage(validationError)
              });
              continue;
            }
            if (!ACCEPTED_MIME_SET.has(entry.contentType)) {
              patchFile(entry.key, {
                stage: 'error',
                error: `Unsupported file type (${entry.contentType})`
              });
              continue;
            }
            seenSha256.add(entry.sha256);
            uploadable.push(entry);
          }

          const toUpload = uploadable.filter((entry) => !controllers.get(entry.key)?.signal.aborted);
          if (toUpload.length === 0) return;
          const batch = await uploadBatchApi.createBatch(
            toUpload.map(({ file, contentType, sha256 }) => ({
              name: file.name,
              size: file.size,
              contentType,
              sha256
            })),
            callDocumentType,
            { groupId, pamQuestionId, pamSiteId, addToOrganisation }
          );
          for (const rejected of batch.rejected ?? []) {
            const entry = withHashes.find((e) => e.file.name === rejected.name);
            if (entry) {
              patchFile(entry.key, { stage: 'error', error: rejected.detail });
            }
          }

          // Pair each batch SAS entry with its uploadable entry (positional)
          const fileInfos = batch.files.map((sasInfo, i) => ({
            ...toUpload[i],
            sasInfo
          }));
          for (const { key, sasInfo } of fileInfos) {
            fileIdsRef.current.set(key, sasInfo.file_id);
            patchFile(key, { documentId: sasInfo.file_id });
          }

          // 3. Upload blobs with bounded concurrency
          let queueIdx = 0;
          const successfulFileIds: string[] = [];

          async function uploadOne() {
            while (queueIdx < fileInfos.length) {
              const idx = queueIdx++;
              const { key, file, sasInfo } = fileInfos[idx];
              const signal = controllers.get(key)?.signal;
              if (signal?.aborted) continue;
              try {
                await uploadBatchApi.uploadToBlob(
                  sasInfo.upload_url,
                  file,
                  (pct) => {
                    patchFile(key, { uploadProgress: pct });
                  },
                  signal
                );
                if (signal?.aborted) continue;
                patchFile(key, { uploadProgress: 100 });
                successfulFileIds.push(sasInfo.file_id);
              } catch (err: any) {
                if (signal?.aborted) continue;
                patchFile(key, {
                  stage: 'error',
                  error: err?.message ?? 'Blob upload failed'
                });
              }
            }
          }

          await Promise.all(Array.from({ length: Math.min(concurrency, fileInfos.length) }, uploadOne));

          const finishedIds = successfulFileIds.filter((fileId) => {
            const info = fileInfos.find((item) => item.sasInfo.file_id === fileId);
            return info != null && !controllers.get(info.key)?.signal.aborted;
          });
          if (finishedIds.length > 0) {
            for (const { key, sasInfo } of fileInfos) {
              if (finishedIds.includes(sasInfo.file_id)) {
                // Set documentId now (not just at 'ready') so that the live
                // fileId matches the backend ID used by resumed-batch polling,
                // preventing duplicate rows in the progress widget.
                patchFile(key, { stage: 'scanning', documentId: sasInfo.file_id });
              }
            }
            await uploadBatchApi.completeBatchUploads(batch.batch_id, finishedIds);

            onBatchReadyRef.current?.(
              batch.batch_id,
              fileInfos
                .filter(({ sasInfo: s }) => finishedIds.includes(s.file_id))
                .map(({ key, file, sasInfo: s }) => ({
                  key,
                  fileId: s.file_id,
                  name: file.name,
                  size: file.size
                }))
            );
          }

          // 5. Run scan → promote → optional DI → onFileReady in parallel
          await Promise.all(
            fileInfos.map(async ({ key, file, contentType, isImage, sasInfo }) => {
              if (!finishedIds.includes(sasInfo.file_id)) return;
              if (controllers.get(key)?.signal.aborted) return;
              const documentId = sasInfo.file_id;
              const signal = controllers.get(key)?.signal;
              if (!signal) return;
              try {
                const hubStatus = await waitForHubReady(batch.batch_id, sasInfo.file_id, signal);
                if (HUB_FAILED_STATUSES.has(hubStatus)) {
                  throw new Error(
                    hubStatus === 'infected'
                      ? 'File was rejected by the security scan.'
                      : `File processing failed (status: ${hubStatus})`
                  );
                }

                const shouldSkipDI = skipDI ? skipDI(file, contentType) : isImage;
                if (!shouldSkipDI) {
                  patchFile(key, { stage: 'processing' });
                  await waitForDocumentIntelligence(documentId, signal);
                }

                if (onFileReady) {
                  patchFile(key, { stage: 'processing' });
                  await onFileReady(documentId, file, key);
                }

                patchFile(key, { stage: 'ready', documentId });
              } catch (err: any) {
                if (signal.aborted) return;
                patchFile(key, {
                  stage: 'error',
                  error: formatUploadErrorDetail(err?.response?.data?.detail ?? err?.message)
                });
              }
            })
          );
        } catch (err: any) {
          if ([...controllers.values()].every((fileController) => fileController.signal.aborted)) return;
          const detail = formatUploadErrorDetail(err?.response?.data?.detail ?? err?.message);
          const failedName = parseBatchValidationFileName(detail);
          for (const { key, file } of entries) {
            if (uploadsRef.current.get(key)?.stage !== 'uploading') continue;
            if (failedName && file.name !== failedName) continue;
            patchFile(key, { stage: 'error', error: detail });
          }
        } finally {
          // If the upload was aborted and a batch was created, clean up any
          // PENDING_UPLOAD rows and their blobs. Best-effort — errors are silent.
          for (const [key, fileController] of controllers) {
            const fileId = fileIdsRef.current.get(key) ?? uploadsRef.current.get(key)?.documentId;
            if (fileController.signal.aborted && fileId) {
              uploadBatchApi.cancelFile(fileId).catch(() => {});
            }
          }
          for (const { key } of entries) {
            inflightRef.current.delete(key);
            abortRef.current.delete(key);
            fileIdsRef.current.delete(key);
          }
        }
      })();

      // Register the same promise for all keys so waitForAll resolves correctly
      for (const { key } of entries) {
        inflightRef.current.set(key, promise);
      }

      return entries.map(({ key }) => key);
    },
    [documentType, onFileReady, skipDI, patchFile, setUploads]
  );

  // -------------------------------------------------------------------------
  // File management
  // -------------------------------------------------------------------------

  const cancelFile = useCallback((key: string) => {
    abortRef.current.get(key)?.abort();
    abortRef.current.delete(key);
    inflightRef.current.delete(key);
  }, []);

  const removeFile = useCallback(
    (key: string) => {
      cancelFile(key);
      setUploads((prev) => {
        const existing = prev.get(key);
        if (existing?.previewUrl) URL.revokeObjectURL(existing.previewUrl);
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    },
    [cancelFile, setUploads]
  );

  const clearUploads = useCallback(
    ({ keepInflight = false, revokePreviewUrls = true }: ClearUploadsOptions = {}) => {
      if (keepInflight) {
        setUploads((prev) => {
          const next = new Map<string, HubFileState>();
          for (const [key, upload] of prev) {
            if (inflightRef.current.has(key)) {
              next.set(key, revokePreviewUrls ? { ...upload, previewUrl: undefined } : upload);
            }
            if (revokePreviewUrls && upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
          }
          return next;
        });
      } else {
        for (const controller of abortRef.current.values()) controller.abort();
        abortRef.current.clear();
        inflightRef.current.clear();
        if (revokePreviewUrls) {
          for (const upload of uploadsRef.current.values()) {
            if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
          }
        }
        setUploads(new Map());
      }
    },
    [setUploads]
  );

  const waitForAll = useCallback(async () => {
    const promises = Array.from(new Set(inflightRef.current.values()));
    if (promises.length > 0) await Promise.all(promises);
  }, []);

  const getUploads = useCallback(() => uploadsRef.current, []);

  return {
    uploads,
    uploadFiles,
    removeFile,
    cancelFile,
    clearUploads,
    waitForAll,
    getUploads
  };
}
